import React from 'react';
import { Upload, Check, X } from 'lucide-react';

const DocumentsStep = ({ formData = {}, handleInputChange, isPharmacy = false }) => {
    const documents = [
        { name: 'haLicenseFile', label: 'Health Authority License', required: true },
        { name: 'tradeLicenseFile', label: 'Trade License', required: true },
        { name: 'signatoryEmiratesIdFile', label: "Emirates ID of Authorised Signatory", required: true },
        { name: 'tariffListFile', label: 'Tariff List', required: !isPharmacy }, // Optional for pharmacy
        { name: 'otherFiles', label: 'Other Documents (optional)', required: false },
    ];

    return (
        <div className="space-y-8 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Upload className="w-6 h-6 text-primary" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                        Upload Required Documents
                    </h2>
                    <p className="text-base text-gray-600 mt-1">
                        Please upload clear, valid copies of the following documents
                    </p>
                </div>
            </div>

            {/* Documents List */}
            <div className="bg-white border-2 border-gray-100 rounded-3xl p-6 md:p-8 shadow-xl space-y-6">
                {documents.map((doc) => {
                    // const file = formData[doc.name];
                    // const hasFile = !!file;

                    const file = formData[doc.name];
                    const isValidFile = file instanceof File;
                    const displaySize = isValidFile && Number.isFinite(file.size)
                        ? (file.size / 1024 / 1024).toFixed(2)
                        : null;

                    const hasFile = isValidFile && displaySize !== null;

                    return (
                        <div
                            key={doc.name}
                            className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-6 transition-all hover:border-primary/50"
                        >
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div className="flex-1">
                                    <label className="block text-base font-semibold text-gray-800 mb-2">
                                        {doc.label}{' '}
                                        {doc.required && <span className="text-primary">*</span>}
                                    </label>
                                    <p className="text-sm text-gray-600 mb-4">
                                        {doc.required
                                            ? 'Required – PDF, JPG, PNG'
                                            : 'Optional – additional supporting documents'}
                                    </p>

                                    <label className="cursor-pointer inline-block">
                                        <input
                                            type="file"
                                            name={doc.name}
                                            onChange={handleInputChange}
                                            className="hidden"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            // ── KEY FIX: Force re-mount when file changes
                                            key={file ? `${file.name}-${Date.now()}` : 'empty'}
                                        />
                                        <div
                                            className={`
                                                flex items-center gap-3 px-6 py-3 rounded-xl border-2 transition-all duration-200 shadow-sm
                                                ${hasFile
                                                    ? 'bg-primary/5 border-primary/50 text-primary'
                                                    : 'bg-white border-gray-300 hover:border-primary text-gray-700'
                                                }
                                            `}
                                        >
                                            <Upload size={20} />
                                            <span className="font-medium">
                                                {hasFile ? 'Change / Re-upload File' : 'Choose File'}
                                            </span>
                                        </div>
                                    </label>

                                    {/* Show file info */}
                                    {hasFile && (
                                        <div className="mt-3 text-sm text-gray-700 flex items-center gap-3">
                                            <span className="font-medium truncate max-w-[220px]">{file.name}</span>
                                            <span className="text-gray-500">
                                                ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* File Status & Remove */}
                                <div className="text-right min-w-[180px]">
                                    {hasFile ? (
                                        <div className="flex items-center justify-end gap-3">
                                            <div className="flex items-center gap-2 text-primary font-medium">
                                                <Check size={18} />
                                                <span>Selected</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    handleInputChange({
                                                        target: { name: doc.name, files: [] },
                                                    });
                                                }}
                                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                                title="Remove file"
                                            >
                                                <X size={18} />
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="text-sm text-gray-500">
                                            No file chosen
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Helpful Note */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-6">
                <p className="text-sm text-gray-700">
                    <strong className="text-gray-900">Important:</strong>
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-1 text-sm text-gray-600">
                    <li>Ensure all documents are clear, legible, and not expired</li>
                    <li>Supported formats: PDF, JPG, JPEG, PNG</li>
                    <li>Required documents must be uploaded before final submission</li>
                    <li>You can re-upload the same file by clicking "Change / Re-upload File"</li>
                </ul>
            </div>
        </div>
    );
};

export default DocumentsStep;


